/*
 * AI 填表员 — 类型定义
 */

/** 文件源：本地 File 对象 或 远程 URL */
export type FileSource = File | string

/** 支持的文档类型 */
export type DocType = 'docx' | 'xlsx'

/** 占位框（后端解析返回） */
export interface Placeholder {
  /** 占位编号（1, 2, 3...） */
  id: number
  /** 所在行（0-based） */
  row: number
  /** 所在列（0-based） */
  col: number
  /** 单元格引用（A1, B2...） */
  cellRef: string
  /** 标签（清洗前/清洗后/拍摄场景...） */
  label: string
  /** 所属组（A=排油烟罩, B=挡火板...） */
  groupId?: string
  groupName?: string
  /** 已填入的照片 URL（填充阶段才有） */
  photoUrl?: string
}

/** 文档解析结果 */
export interface DocumentParseResult {
  /** 文件 URL（OSS 地址） */
  fileUrl: string
  /** 文档类型 */
  docType: DocType
  /** 文档标题 */
  title: string
  /** 占位列表 */
  placeholders: Placeholder[]
  /** 项目组数量（如 4 个清洗项目） */
  groupCount: number
  /** 类型数量（如 2 种：清洗前 / 清洗后） */
  typeCount: number
}

/** 模板（首页模板库） */
export interface Template {
  id: string
  name: string
  emoji: string
  /** 封面渐变色 */
  coverGradient: 'blue' | 'orange' | 'green' | 'purple'
  /** 占位数量 */
  placeholderCount: number
  /** 创建日期（10 月 26 日） */
  createdAt: string
  /** 已使用次数 */
  usedCount: number
}

/** 历史成品 */
export interface ExportedFile {
  id: string
  fileName: string
  fileType: DocType
  fileSize: number
  cdnUrl: string
  createdAt: string
}

/** user_ask 结构化问题 */
export interface UserAskQuestion {
  qid: string
  question: string
  /** 短标签 (3-6 字), 用户提交回执时作为答案标签显示, e.g. "时间范围" / "标段".
   *  后端缺失时前端回退到 question 全文 (UserAskResponseCard 显得啰嗦). */
  label_short?: string
  type:
    | 'single_choice'
    | 'multi_choice'
    | 'date_range'
    | 'text'
    | 'confirm'
    | 'keyword_select'
    | 'person_select'
    | 'picker'
  options?: Array<{
    value: string
    label?: string
    reason?: string
    /** 选中此项时展开内联输入控件
     *  - 'text': 文本框
     *  - 'date_range': 日期范围选择器（含自然语言输入）
     *  提交时，若内联输入有值则用其覆盖 option.value 作为最终答案。
     */
    expands?: 'text' | 'date_range'
  }>
  default?: any
  allow_custom?: boolean
  hint?: string
  /**
   * type='picker' 时的元数据。前端会按 kind 弹对应 Modal 让用户从全集选:
   *   - department_member → DepartmentMemberPickerV2 (成员 + 部门混选)
   *   - watermark         → WatermarksPicker
   * 候选数量 > 3 时 LLM 必须用 picker, 不要硬塞 multi_choice (硬上限 3 项).
   */
  picker?: {
    kind: 'department_member' | 'watermark'
    /** LLM 已粗识别到的候选, Modal 打开时预勾上 */
    preselected?: {
      user_ids?: string[]
      department_ids?: string[]
      watermark_ids?: string[]
    }
    /** 最少选 N 个, 前端拦截 submit */
    min?: number
    /** 最多选 N 个 */
    max?: number
  }
}

/**
 * picker 题用户提交后的 answer value 形态 (D 方案: 显式 ids + summary, 瘦协议)。
 *
 *  - ids 字段是 string[], LLM 调下游业务工具时 .join(",") 出 csv;
 *  - summary 是人类可读字符串, 既给 SubmittedSummaryView 回显, 也给 LLM 后续
 *    生成"基于刚刚您选择的市场部成员..."回复时直接读, 避免复述 id。
 *  - 不冗余存 {id,name}[] 进协议: 完整对象由 PickerField 在本地 React state
 *    持有用于 chip 列名字回显, 不进网络协议层。
 */
export interface PickerAnswerValue {
  kind: 'department_member' | 'watermark'
  user_ids?: string[]
  department_ids?: string[]
  watermark_ids?: string[]
  summary: string
}

/** 消息 Part（对齐 XHeyClaw-Web / assistant-ui UIMessage）
 *
 *   text: markdown 文本段
 *   reasoning: Qwen 等模型的思考过程
 *   tool-call: 工具调用（含状态流转）
 *   system-error: 系统级错误提示卡 (网络断 / quota / 上游 LLM 异常等);
 *                 跟 text 区分以便 MessageBubble 渲染成错误卡片而非普通文字;
 *                 内容仅含用户可见的中性文案, **不存原始 errorText**
 *                 (raw 已在 useSSEChat 里 console.warn 给开发, 不允许进入 UI 层)
 */
export type MessagePart =
  | { type: 'text'; text: string }
  | {
      type: 'system-error'
      /** 已经过 classifyErrorMessage 中性化的用户可见文案 */
      message: string
    }
  | {
      /**
       * 用户消息附件（图片 / 文档）。对齐 XHeyClaw-Web /api/chat 请求体结构，
       * 后端 BFF 会把 image/* 注入到 text 里、其它文件转成"附件文件:"清单。
       * 前端用户气泡也按 file part 来渲染缩略图 / 文件卡片。
       */
      type: 'file'
      url: string
      mediaType: string
      name: string
    }
  | {
      type: 'reasoning'
      text: string
      /** 流式中 / 完成 */
      state?: 'streaming' | 'done'
    }
  | {
      type: 'tool-call'
      toolCallId: string
      toolName: string
      /** 完整 args 对象（tool-input-available 后才有） */
      args?: Record<string, unknown>
      /** 拼接中的 JSON 文本（tool-input-delta 流式片段） */
      argsText?: string
      /** 状态：参数流中 / 参数就位 / 输出就位 / 出错 / 中断 */
      state:
        | 'input-streaming'
        | 'input-available'
        | 'output-available'
        | 'output-error'
        | 'incomplete'
      output?: unknown
      errorText?: string
      /** 取消原因（incomplete 时） */
      cancelReason?: string
      startedAt?: number
      endedAt?: number
      /** 动态操作描述（后端 xhey_tool_call_status.activityDescription，
       *  status=running 时附带）—— UI 上优先用它当 label / spinner 文案，
       *  缺失时回退到 toolName 原始名 */
      activityDescription?: string
      /** 紧凑视图标题（后端 xhey_tool_call_status.toolUseSummary，
       *  status=completed/failed 时附带） */
      toolUseSummary?: string
      /** 后端测量的真实耗时（ms，xhey_tool_call_status.durationMs），
       *  优先于前端用 endedAt - startedAt / 客户端测量算出来的值 */
      durationMs?: number
      /** 长时工具执行进度（done/total），后端 xhey_tool_call_status.progress，
       *  status=running 时按 toolCallId 增量更新 —— 大批量填图等"几分钟无反馈"场景渲染进度条 */
      progress?: { done: number; total: number; label?: string }
    }
  | {
      type: 'user-ask'
      pendingId: string
      title: string
      description?: string
      questions: UserAskQuestion[]
      autoFillPreview?: Array<{ label: string; value: string }>
      /** user has already submitted (read-only in history) */
      submitted?: boolean
      /**
       * 渲染模式:
       *  - 'chip' (默认,新协议): chat 流轻量 chip("在底部确认 ↓"),完整卡片走 UserAskActionSheet
       *  - 'inline' (legacy): chat 流直接渲染 UserAskBlock 完整卡片
       * 用户已提交后 (submittedAnswers 有值),两种模式都走 UserAskBlock submitted 态回看.
       */
      displayAs?: 'chip' | 'inline'
    }

/** 聊天消息（SSE 流） */
export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  /** 消息的结构化内容（对齐 XHeyClaw-Web parts 模型） */
  parts: MessagePart[]
  /** 附加渲染内容（识别结果卡、快捷按钮等） */
  attachments?: ChatAttachment[]
  /** 是否流式中 */
  streaming?: boolean
  /** 流式开始时间（用于 ThinkingIndicator "思考中…Ns"） */
  thinkingStartedAt?: number
  createdAt: number
  /** 流式结束时后端带的耗时 */
  durationMs?: number
  /** 输出 token */
  tokenCount?: number
  /** 输入 token */
  promptTokens?: number
}

/** 取 message 的 markdown 文本（所有 text part 拼接，忽略 reasoning / tool-call） */
export function getTextContent(msg: ChatMessage): string {
  if (!msg.parts || msg.parts.length === 0) return ''
  return msg.parts
    .filter(
      (p): p is Extract<MessagePart, { type: 'text' }> => p.type === 'text'
    )
    .map((p) => p.text)
    .join('')
}

/** 单个 part 判类型辅助 */
export function isTextPart(
  p: MessagePart
): p is Extract<MessagePart, { type: 'text' }> {
  return p.type === 'text'
}
export function isReasoningPart(
  p: MessagePart
): p is Extract<MessagePart, { type: 'reasoning' }> {
  return p.type === 'reasoning'
}
export function isToolCallPart(
  p: MessagePart
): p is Extract<MessagePart, { type: 'tool-call' }> {
  return p.type === 'tool-call'
}
export function isUserAskPart(
  p: MessagePart
): p is Extract<MessagePart, { type: 'user-ask' }> {
  return p.type === 'user-ask'
}

/** 会话（对齐 Platform Shared Instance /api/sessions） */
export interface SessionItem {
  /** 会话 ID（也作为 conversation_id 用于 chat 接口的 X-Conversation-Id） */
  id: string
  /** 会话标题 */
  title: string
  /** ISO 8601 字符串，如 "2026-04-27T10:30:00" */
  updated_at: string
  /** 消息条数 */
  message_count: number
}

/** 用户技能卡片（对齐 /api/user-skills，作为"我的模板库"数据源）
 *
 * description vs summary:
 *   - description: LLM 触发判断用 (触发词列表), UI 不展示
 *   - summary: 用户业务描述, UI 卡片 / 编辑 Modal 展示
 *   - 旧 skills 没 summary 字段, fallback 到 description (但显示效果差)
 */
export interface SkillItem {
  name: string
  display_name: string
  description?: string
  summary?: string
  category?: string
  size_bytes?: number
  /** ms 时间戳 */
  updated_at?: number
}

/** 技能详情（对齐 GET /api/user-skills/:name，含 SKILL.md body） */
export interface SkillDetail {
  name: string
  display_name: string
  description?: string
  summary?: string
  category?: string
  /** SKILL.md body 部分（不含 frontmatter）— 编辑 Modal 不展示，避免用户改坏内部协议 */
  content?: string
}

/** 技能 reference 文件（对齐 GET /api/user-skills/:name/references） */
export interface SkillReferenceItem {
  filename: string
  size_bytes?: number
  /** ms 时间戳 */
  updated_at?: number
  download_url?: string
  preview_url?: string
}

/** 文件条目（对齐 /api/files，作为"最近填好的文档"数据源） */
export interface FileItem {
  path: string
  filename: string
  /** 显示名(AI/用户命名),对齐 upload 响应的 original_name */
  original_name?: string
  size_bytes?: number
  mime?: string
  /** ms 时间戳 */
  mtime_ms?: number
  /** 后端 signed URL，不依赖 cookie，跨域可直接 <a download> 拉文件本体 */
  download_url?: string
  preview_url?: string
  /** 工作副本 id(对齐 upload 响应),有则可走 rename API;后端 2026-07 补充字段 */
  working_id?: string
}

/** 后端返回的历史消息（/api/sessions/:id/history 每条） */
export interface HistoryMessage {
  id?: string
  role: 'user' | 'assistant'
  parts?: MessagePart[]
  /** 兼容纯文本历史：没 parts 时 fallback */
  content?: string
  /** ISO 8601 或数字毫秒，hydrateHistory 兼容两种 */
  created_at?: number | string
}

/** 聊天附加内容（AI 通过 SSE 流式推送的「卡片」）
 *
 * 设计原则：所有「业务阶段」都通过 AI 发不同的附件卡片来体现，
 * 前端不区分 chat/export 状态，统一渲染附件即可。
 */
export type ChatAttachment =
  // ─── 识别阶段：8 个照片位 / 4 个项目组 / 2 种类型
  | {
      type: 'recognition-result'
      data: {
        placeholders: number
        groups: number
        types: number
        note?: string // "✦ 我注意到每对照片有「前后」时序"
      }
    }
  // ─── 快捷回复（AI 给的预设按钮，点击即发送对应 action）
  | {
      type: 'quick-replies'
      replies: Array<{ label: string; action: string; primary?: boolean }>
    }
  // ─── 进度步骤（解析中、填充中等场景）
  | {
      type: 'progress-steps'
      steps: Array<{
        title: string
        desc?: string
        status: 'done' | 'doing' | 'pending'
      }>
    }
  // ─── 完成统计卡（用时 / 节省 / 完成度）
  | {
      type: 'stat-card'
      data: {
        timeUsed?: string // "1 分 23 秒"
        timeSaved?: string // "比手动填省了 28 分钟"
        confirmed?: string // "8/8"
        confirmedDesc?: string // "照片已全部确认"
      }
    }
  // ─── 导出面板（格式选择 + 下载按钮）
  | {
      type: 'export-panel'
      data: {
        formats: Array<{
          key: string
          label: string
          ext: string
          emoji?: string
        }>
        defaultFormat?: string
        fileName?: string
        fileSize?: string // "约 2.4 MB"
      }
    }
  // ─── 保存为模板
  | {
      type: 'template-save'
      data: {
        suggestedName: string
        benefits?: string[] // ["✓ 这 4 个照片位置", "✓ 「前/后」时序"]
      }
    }
  // ─── 分享到 IM
  | {
      type: 'share-targets'
      targets: Array<{
        key: string
        label: string
        emoji?: string
        tone?: 'blue' | 'green' | 'orange' | 'neutral'
      }>
      /** 要分享的链接（链接/邮件/微信/钉钉 都用这个 URL） */
      shareUrl?: string
      /** 邮件主题，默认"AI 填表员生成的文档" */
      emailSubject?: string
      /** 卡头标题，默认"发送给" */
      title?: string
      /** 副标题，默认"快速把结果给到客户或团队" */
      description?: string
    }
  // ─── 文件附件（成品下载链接）
  | {
      type: 'file'
      data: { name: string; url: string; size?: string }
    }
